import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent, RumEventDomainContext } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import type { CartMutationType } from '@/utils/useCartMutations';
import type { CartPricingDisableReason } from '@/utils/cartPricing';

declare global {
  interface Window {
    // Ketch CMP. Present (as a queue stub) once the Smart Tag boots; a function.
    ketch?: (action: string, ...args: unknown[]) => void;
  }
}

const FIRST_PARTY_STACK_PATTERN =
  /(store(?:-[a-z0-9-]+)?\.23andme|localhost|\/static\/|braintree)/i;

// A V8 stack is "<message>\n    at fn (url:line:col)\n    at ...". Testing the
// pattern against the whole string (as this used to) tags an event first-party
// whenever ANY frame matches, even when the real throw site is a frame further
// up the stack that merely calls into store code (e.g. a browser extension's
// monkey-patched window.fetch invoked from a GTM script) — BKO-5566.
// Classify off the throwing (topmost) frame only. Falls back to the full
// string when there is no "at ..." frame line, e.g. a bare message.
const getThrowingFrame = (stack: string): string =>
  stack.split('\n').find((line) => /^\s*at\s/.test(line)) ?? stack;

// Braintree renders each credit-card field (number / expiration / CVV) as a
// cross-origin <iframe> injected into a `.spc-hosted-field` box. A tap that
// lands inside that iframe never reaches the parent page, so the "dead clicks"
// RUM records on the field are the taps that just MISSED the iframe and hit the
// surrounding parent chrome — either the `.spc-hosted-field` box itself or its
// `.spc-input-container` wrapper (which also holds the hidden label and the card
// illustration). Both are benign artifacts of tapping "the card field", and
// they dominate the payment-form frustration signal (~6k CVV + card-number dead
// clicks / 14d on renewal-opt-in alone), drowning the real dead/rage clicks on
// Continue and the "Save your credit card info" checkbox. BKO-4904 bucket C:
// drop them at the source so the frustration signal is trustworthy.
const HOSTED_FIELD_SELECTOR = '.spc-hosted-field';
const FIELD_CONTAINER_SELECTOR = '.spc-input-container';

/**
 * True when the click target sits on (or in the wrapper of) a Braintree
 * hosted-field box. Only the three card fields wrap a `.spc-hosted-field`, so
 * ordinary text inputs and the CVV help tooltip (a separate `.spc-input`) are
 * unaffected. Matches the field box / injected iframe directly, and the field's
 * `.spc-input-container` when the miss lands on the label or card illustration.
 */
const isHostedFieldTarget = (target: EventTarget | null | undefined): boolean => {
  if (!(target instanceof Element)) return false;
  if (target.closest(HOSTED_FIELD_SELECTOR)) return true;
  const container = target.closest(FIELD_CONTAINER_SELECTOR);
  return container !== null && container.querySelector(HOSTED_FIELD_SELECTOR) !== null;
};

/**
 * True only for a *pure* dead click landing on a Braintree hosted-field. A click
 * that also carries rage_click / error_click is left alone — that is the real
 * signal for a field that never mounted (the BKO-4904 bucket A failure), which
 * we must not hide.
 */
const isBenignHostedFieldDeadClick = (event: RumEvent, context: RumEventDomainContext): boolean => {
  if (event.type !== 'action') return false;

  const frustrations = event.action?.frustration?.type;
  if (!frustrations || frustrations.length !== 1 || frustrations[0] !== 'dead_click') {
    return false;
  }

  // Action domain context carries the originating DOM event(s); inspect the
  // real click target rather than the locale-dependent action name text.
  return isHostedFieldTarget((context as { events?: Event[] }).events?.[0]?.target);
};

/**
 * RUM beforeSend hook. Extracted from init() so it can be unit-tested (init is
 * mocked in tests). Drops benign hosted-field dead clicks (BKO-4904 C) and
 * tags errors as first- vs third-party so the GEM-OMG dashboards can split them.
 */
export const rumBeforeSend = (event: RumEvent, context: RumEventDomainContext): boolean => {
  if (isBenignHostedFieldDeadClick(event, context)) {
    return false;
  }

  if (event.type === 'error') {
    const stack = event.error?.stack || event.error?.message || '';
    const isFirstParty = FIRST_PARTY_STACK_PATTERN.test(getThrowingFrame(stack));

    event.context = {
      ...(event.context || {}),
      first_party: isFirstParty,
      third_party: !isFirstParty,
      classified_by: 'error-1p-or-3p',
    };
  }

  return true;
};

export type WalletMethod = 'applepay' | 'paypal' | 'venmo' | 'googlepay';
export type PaymentMethod = WalletMethod | 'new_cc' | 'saved_cc';
export type PaymentFlow =
  | 'kit_checkout'
  | 'labs_checkout'
  | 'subscription'
  | 'upgrade_subscription'
  | 'renew_subscription'
  | 'renewal_activation'
  | 'unknown';
export type PaymentPhase = 'attempt' | 'success' | 'failure' | 'cancel' | 'fallback';

export interface TrackPaymentContext {
  method: PaymentMethod;
  flow?: PaymentFlow;
  cartId?: string;
  subscriptionId?: string;
  errorCode?: string;
  errorMessage?: string;
  recurringRequested?: boolean;
  recurringSent?: boolean;
  error?: string;
}

// Every payment-bearing route in routes.tsx needs its own branch below, or its
// trackPayment calls silently land in the 'unknown' bucket (BKO-4477).
export const getPaymentFlow = (pathname: string = window.location.pathname): PaymentFlow => {
  if (/\/upgrade-subscription\/payment(?:\/|$)/.test(pathname)) {
    return 'upgrade_subscription';
  }
  if (/\/renew-subscription\/payment(?:\/|$)/.test(pathname)) return 'renew_subscription';
  if (/\/renewal-opt-in(?:\/|$)/.test(pathname)) return 'renewal_activation';
  if (/\/subscription\/payment(?:\/|$)/.test(pathname)) return 'subscription';
  if (/\/labs\/checkout(?:\/|$)/.test(pathname)) return 'labs_checkout';
  if (/\/checkout(?:\/|$)/.test(pathname)) return 'kit_checkout';
  return 'unknown';
};

export const trackPayment = (phase: PaymentPhase, ctx: TrackPaymentContext): void => {
  const flow = ctx.flow ?? getPaymentFlow();
  datadogRum.addAction(`payment_${phase}`, { ...ctx, flow });
};

export type { CartMutationType };
export type CartMutationOutcome = 'success' | 'failure' | 'refused';

export interface TrackCartMutationContext {
  type: CartMutationType;
  outcome: CartMutationOutcome;
  /** Ms the shopper waited: click→settle for a queued write, apply→settle otherwise. */
  durationMs?: number;
  /** Reconcile round-trip after a refusal, kept out of `durationMs` so the baseline stays comparable. */
  recoveryMs?: number;
  /** The share of `durationMs` spent parked behind another write; absent when unqueued. */
  queuedMs?: number;
  /** Click→optimistic-commit. Absent when nothing was applied optimistically, so the
   * flagged cohort's apply budget is read off this and never off `durationMs`. */
  applyMs?: number;
  /** False when the settled kit set disagrees with what the optimistic apply predicted.
   * Absent when there was no prediction to compare, which is not the same as agreement. */
  itemHashMatched?: boolean;
  optimisticItemCount?: number;
  settledItemCount?: number;
  /** False when the client-computed totals disagree with the settled ones. Alertable apart from
   * `itemHashMatched`: a wrong figure is worse than a wrong row (BKO-5360). */
  totalsMatched?: boolean;
  predictedSubtotal?: number;
  predictedSubtotalWithDiscount?: number;
  settledSubtotal?: number;
  settledSubtotalWithDiscount?: number;
  /** Which check latched compute off, so a stopped session is distinguishable from one that
   * never computed (BKO-5360). */
  pricingComputeDisabledReason?: CartPricingDisableReason;
  /** Contract: `docs/pricing-api.md` § What a client may compute from the matrix. */
  upsellMatched?: boolean;
}

/**
 * Emit a `cart_mutation` RUM action for one cart change (add / remove / upgrade /
 * downgrade), carrying its type, outcome, and the shopper's wait. The pre/post
 * cart-latency instrument for the optimistic-cart work (epic BKO-5047); pure
 * instrumentation, no behavior change. Sibling of `trackPayment`. `refused` means the
 * request resolved but its body was not cart-shaped, so nothing was applied (BKO-5346).
 */
export const trackCartMutation = (ctx: TrackCartMutationContext): void => {
  datadogRum.addAction('cart_mutation', { ...ctx });
};

// Its own action because it is not a mutation outcome: it can fire on a cart nobody has
// touched, and it never changes what the shopper is shown (BKO-5360).
export const trackCartPricingSkuMismatch = (keys: string[]): void => {
  datadogRum.addAction('cart_pricing_sku_mismatch', { keys, count: keys.length });
};

// Its own action, not a `cart_mutation` field: that context is keyed on a mutation type a
// wallet tap does not have (BKO-5355).
export const trackWalletTapBlocked = (method: WalletMethod): void => {
  datadogRum.addAction('cart_wallet_tap_blocked', { method });
};

export type CheckoutBarrierOutcome = 'clear' | 'settled' | 'timeout' | 'failed';

export interface TrackCheckoutBarrierContext {
  /** Which affordance was pressed; `wallet` also carries the `method` that was tapped. */
  path: 'checkout' | 'wallet';
  method?: string;
  outcome: CheckoutBarrierOutcome;
  /** Click→release. Zero on `clear`, which is what separates a no-wait submit from a short one. */
  waitedMs: number;
  /** Writes outstanding at the click: the dispatched one plus anything parked behind it. */
  pendingOps: number;
  /** Set on the submit that follows a timeout dialog: continued vs abandoned. */
  afterTimeout?: boolean;
}

/** Emit `cart_checkout_barrier` for one submit, `clear` included — the denominator is the point,
 * and no pre-launch number stands in: today's is taken through a click blocker (BKO-5356). */
export const trackCheckoutBarrier = (ctx: TrackCheckoutBarrierContext): void => {
  datadogRum.addAction('cart_checkout_barrier', { ...ctx });
};

/**
 * Extract Datadog view name from URL pathname
 * Format: "region/routeName" (e.g., "en-us/cartv2", "en-ca/checkout")
 *
 * @param pathname - URL pathname (e.g., "/en-us/cartv2")
 * @returns View name or null if pathname is invalid
 */
export const getDatadogViewName = (pathname: string): string | null => {
  const parts = pathname.split('/').filter(Boolean);

  if (parts.length >= 2) {
    // parts[0] is region (e.g., "en-us"), parts[1+] is route (e.g., "cartv2")
    const region = parts[0];
    const routeParts = parts.slice(1);
    const routeName = routeParts.join('/');

    if (!routeName) {
      return null; // No route after region = invalid path
    }

    return `${region}/${routeName}`;
  }

  return null;
};

let rumInitialized = false;

/**
 * Initialize Datadog RUM with configuration from Django backend.
 * Idempotent — safe to call again once consent resolves.
 */
const startDatadogRum = (): void => {
  if (rumInitialized) return;

  const datadogConfig = window.DATADOG;

  if (!datadogConfig?.ENVIRONMENT) {
    console.warn(
      'Datadog environment configuration not found on window.DATADOG. Skipping RUM initialization. Uncomment DD_ENV in env.yml to enable if in local dev.',
    );
    return;
  }

  try {
    datadogRum.init({
      applicationId: '48eafe9e-67e9-44a5-b03b-10bca7d5dafe',
      clientToken: 'pubf2edffc4f5a1c30268145ff8290a7d7d',
      site: 'datadoghq.com',
      service: 'store',

      env: datadogConfig.ENVIRONMENT,
      version: datadogConfig.VERSION || undefined,

      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      defaultPrivacyLevel: 'mask-user-input',

      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      trackViewsManually: true, // Disable automatic view tracking - we handle it manually
      traceSampleRate: 100, // TODO: revert to 20 after testing RUM-APM correlation
      beforeSend: rumBeforeSend,

      allowedTracingUrls: [window.location.origin],

      plugins: [reactPlugin({ router: false })],
    });

    // init() succeeded — guard against re-init now. The tag/view calls below
    // are best-effort; if init() itself throws, the flag stays false so a
    // later consent event can retry.
    rumInitialized = true;

    // Apply DD_TAGS as global context to all RUM events
    if (datadogConfig.TAGS) {
      Object.entries(datadogConfig.TAGS).forEach(([key, value]) => {
        datadogRum.setGlobalContextProperty(key, value);
      });
    }

    // Set initial view name based on current URL path
    // This ensures the initial page load metrics are captured with the correct view name
    const viewName = getDatadogViewName(window.location.pathname);
    if (viewName) {
      datadogRum.startView({ name: viewName });
    }
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
};

/**
 * PUB-8167: Datadog RUM (incl. Session Replay) is analytics, so gate it behind
 * Ketch analytics consent — the same authority as Heap/CustomerIO. RUM only
 * starts once Ketch resolves analytics consent as granted.
 *
 * Fail closed: if Ketch is absent (unconfigured, e.g. local dev) we never init,
 * so no RUM/Replay is sent without a consent decision. Ketch delivers consent
 * nested under `purposes`, e.g. { purposes: { analytics: true } }.
 */
export const initializeDatadogRum = (): void => {
  if (typeof window.ketch !== 'function') return;

  // Starts RUM on grant. We can't tear RUM down on a later revoke (the SDK has
  // no stop), so a grant-then-revoke session keeps RUM running for that page.
  const onConsent = (consent?: { purposes?: Record<string, boolean> }) => {
    if (consent?.purposes?.analytics) startDatadogRum();
  };

  // Registering must never break app bootstrap (called at module load).
  try {
    window.ketch('getConsent', onConsent);
    window.ketch('on', 'consent', onConsent);
  } catch (error) {
    console.error('Failed to register Datadog RUM consent listener:', error);
  }
};
